// DebtHistory: table of debt entries with balance summary and collect button.
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api/client";
import { formatDate } from "@/lib/format/date";
import { useAuthStore } from "@/lib/auth/store";

interface Entry {
  id: string;
  entryType: string;
  amount: string;
  method?: string;
  note: string;
  dueDate?: string;
  createdAt: string;
}

interface Props {
  customerId: string;
  balance: string;
  onCollect: () => void;
  /** Mở dialog ghi nợ tay (nợ cũ đầu kỳ) — chỉ truyền cho manager/admin. */
  onAddDebt?: () => void;
}

const TYPE_LABEL: Record<string, string> = {
  owe: "Ghi nợ",
  payment: "Trả nợ",
  credit: "Ký gửi",
  adjustment: "Điều chỉnh",
};

function fmtVND(n: string) {
  return new Intl.NumberFormat("vi-VN").format(parseFloat(n) || 0) + " ₫";
}

function signedAmount(entry: Entry) {
  const amt = parseFloat(entry.amount) || 0;
  const isDebit = entry.entryType === "owe" || entry.entryType === "adjustment";
  return isDebit ? amt : -amt;
}

export function DebtHistory({ customerId, balance, onCollect, onAddDebt }: Props) {
  const user = useAuthStore((s) => s.user);
  const isManager = user?.role === "admin" || user?.role === "manager";
  const { data, isLoading } = useQuery({
    queryKey: ["debts", customerId],
    queryFn: () => apiGet<Entry[]>(`/v1/debts?customer_id=${customerId}&limit=50`),
  });

  const entries = data ?? [];
  const balNum = parseFloat(balance);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm text-muted-foreground">Số dư công nợ: </span>
          <span className={`text-lg font-bold ${balNum > 0 ? "text-destructive" : balNum < 0 ? "text-green-600" : ""}`}>
            {fmtVND(balance)}
          </span>
          {balNum > 0 && <span className="ml-2 text-xs text-muted-foreground">(KH đang nợ)</span>}
          {balNum < 0 && <span className="ml-2 text-xs text-green-600">(KH đang dư)</span>}
        </div>
        <div className="flex gap-2">
          {isManager && onAddDebt && (
            <button
              onClick={onAddDebt}
              className="rounded-md border border-amber-500 px-3 py-1.5 text-sm font-medium text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
            >
              Ghi nợ đầu kỳ
            </button>
          )}
          {balNum > 0 && (
            <button
              onClick={onCollect}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
            >
              Thu nợ
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              {["Loại", "Số tiền", "Phương thức", "Ghi chú", "Đến hạn", "Ngày"].map((h) => (
                <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Đang tải...</td></tr>
            )}
            {!isLoading && entries.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Chưa có giao dịch</td></tr>
            )}
            {entries.map((e) => {
              const signed = signedAmount(e);
              return (
                <tr key={e.id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      e.entryType === "owe" ? "bg-red-100 text-red-700" :
                      e.entryType === "payment" ? "bg-green-100 text-green-700" :
                      e.entryType === "credit" ? "bg-blue-100 text-blue-700" :
                      "bg-yellow-100 text-yellow-700"
                    }`}>
                      {TYPE_LABEL[e.entryType] ?? e.entryType}
                    </span>
                  </td>
                  <td className={`px-4 py-2 font-mono font-medium ${signed > 0 ? "text-destructive" : "text-green-600"}`}>
                    {signed > 0 ? "+" : ""}{fmtVND(String(Math.abs(signed)))}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{e.method || "—"}</td>
                  <td className="px-4 py-2 max-w-48 truncate" title={e.note}>{e.note || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{e.dueDate || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {formatDate(e.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
